import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { Zap, Users, Sparkles, ShieldCheck } from "lucide-react";
import AuthHero from "@/components/AuthHero";

export default function Register() {
  const [form, setForm] = useState({ email: "", password: "", name: "", workspace_name: "" });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const [invite, setInvite] = useState(null);
  React.useEffect(() => { if (inviteToken) api.get(`/invites/${inviteToken}`).then((r) => { setInvite(r.data); setForm((f) => ({ ...f, email: r.data.email })); }).catch(() => setInvite({ error: true })); }, [inviteToken]);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(inviteToken ? { ...form, invite_token: inviteToken } : form);
      toast.success("Workspace created");
      navigate("/deals");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] bg-background">
      <div className="flex flex-col p-8 lg:p-12">
      <Link to="/login" className="flex items-center gap-2.5 w-fit">
        <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-md shadow-[0_2px_12px_hsl(var(--primary)/0.45)]"><Zap className="w-4 h-4 text-primary-foreground" fill="currentColor" /></div>
        <span className="display font-bold tracking-tight text-lg">Auto-Close<span className="text-primary">-Ai</span></span>
      </Link>
      <div className="flex-1 flex items-center">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 stagger">
        <div>
          <div className="eyebrow mb-3">Get started</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[0.95]">Create your workspace.</h1>
          <p className="text-sm text-muted-foreground mt-3">Free to start. Invite your team and clients later.</p>
        </div>
        <div className="space-y-2">
          <Label>Your name</Label>
          <Input data-testid="register-name" value={form.name} onChange={upd("name")} required />
        </div>
        <div className="space-y-2">
          <Label>Workspace name</Label>
          {invite && !invite.error ? <div className="text-sm border border-primary/30 bg-primary/10 rounded-md px-3 py-2" data-testid="register-invite-banner">Joining <b>{invite.workspace_name}</b> as {invite.role.replace("_", " ")}</div>
          : invite?.error ? <div className="text-sm text-rose-500 border border-rose-500/30 bg-rose-500/10 rounded-md px-3 py-2" data-testid="register-invite-invalid">This invite link is invalid or already used. Ask the workspace owner for a new invite — or remove <span className="mono">?invite</span> from the URL to create your own workspace.</div>
          : <Input data-testid="register-workspace" value={form.workspace_name} onChange={upd("workspace_name")} placeholder="Acme Sales" />}
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input data-testid="register-email" type="email" value={form.email} onChange={upd("email")} required />
        </div>
        <div className="space-y-2">
          <Label>Password</Label>
          <Input data-testid="register-password" type="password" value={form.password} onChange={upd("password")} required minLength={6} />
        </div>
        <Button data-testid="register-submit" disabled={loading} className="w-full h-10">{loading ? "Creating..." : "Create workspace"}</Button>
        <div className="text-sm text-muted-foreground">
          Have an account? <Link to="/login" className="text-primary font-medium hover:underline underline-offset-4">Sign in</Link>
        </div>
      </form>
      </div>
      <div className="text-xs text-muted-foreground mono">Autonomous AI sales platform + CRM</div>
      </div>
      <AuthHero headline={<>Built for agencies that <span className="text-white/60">close on autopilot.</span></>} points={[{ icon: Users, text: "Owner, member and client-view roles out of the box" }, { icon: Sparkles, text: "Playbooks, automation rules and AI personalization included" }, { icon: ShieldCheck, text: "Deliverability health and warmup keep every inbox safe" }]} />
    </div>
  );
}
